import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteChangeReason } from '@mui/material/useAutocomplete';
import { useFormikContext } from 'formik';
import { differenceBy } from 'lodash';

import {
  ProfileSuggestionFragment,
  UpdateGroupInput,
  useCreateGroupModeratorMutation,
  useDeleteGroupModeratorMutation,
} from 'api';
import { useNotify } from 'hooks';
import { useGroupModerators } from 'features/groups/hooks';
import { useCreateModerator, useDeleteModerator } from 'features/groups/updaters';
import { moderatorIsOwner } from 'features/groups/helpers';

import ModeratorPicker from './ModeratorPicker';

type Event = ChangeEvent;
type Value = ProfileSuggestionFragment[];
type Reason = AutocompleteChangeReason;

const Moderators: FC = () => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { moderators } = useGroupModerators();
  const { values } = useFormikContext<UpdateGroupInput>();

  const createModeratorOptions = useCreateModerator();
  const createModeratorResult = useCreateGroupModeratorMutation(createModeratorOptions);

  const deleteModeratorOptions = useDeleteModerator();
  const deleteModeratorResult = useDeleteGroupModeratorMutation(deleteModeratorOptions);

  const { mutateAsync: createMutation, isLoading: isCreateLoading } = createModeratorResult;
  const { mutateAsync: deleteMutation, isLoading: isDeleteLoading } = deleteModeratorResult;

  const isMutating = isCreateLoading || isDeleteLoading;
  const ownerId = moderators.find(moderator => moderatorIsOwner(moderator))?.user_id;
  const moderatorIds = moderators.map(moderator => moderator.user_id);
  const moderatorProfiles = moderators.map(moderator => ({
    id: moderator.user_id,
    title: moderator.user?.full_name ?? '',
    image: moderator.user?.image ?? null,
  }));

  async function handleCreateModerator(user: ProfileSuggestionFragment) {
    await createMutation({
      group_id: values.id,
      user_id: user.id,
    });

    const message = t('groups:addManagerSuccess', {
      name: user.title,
      group: values.title,
    });

    notify.info({ message });
  }

  async function handleDeleteModerator(user: ProfileSuggestionFragment) {
    await deleteMutation({
      group_id: values.id,
      user_id: user.id,
    });

    const message = t('groups:deleteManagerSuccess', {
      name: user.title,
      group: values.title,
    });

    notify.info({ message });
  }

  function handleChange(event: Event, value: Value, reason: Reason) {
    switch (reason) {
      case 'selectOption': {
        const [userToAdd] = differenceBy(value, moderatorProfiles, 'id');
        void handleCreateModerator(userToAdd);
        break;
      }
      case 'removeOption': {
        const [userToRemove] = differenceBy(moderatorProfiles, value, 'id');
        void handleDeleteModerator(userToRemove);
        break;
      }
    }
  }

  return (
    <ModeratorPicker
      value={moderatorProfiles}
      ownerId={ownerId}
      isMutating={isMutating}
      excludeIds={moderatorIds}
      onChange={handleChange}
    />
  );
};

export default Moderators;
